const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8083';

function authHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function req(method, path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

async function uploadFile(path, file) {
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: authHeaders(),
    body: formData,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Upload failed');
  return data;
}

export const api = {
  // Auth
  signup: (body) => req('POST', '/api/auth/signup', body),
  login:  (body) => req('POST', '/api/auth/login', body),
  logout: ()     => req('POST', '/api/auth/logout'),
  me:     ()     => req('GET',  '/api/auth/me'),

  // Discovery
  channels: (category, tag) => req('GET', `/api/channels${tag ? `?tag=${encodeURIComponent(tag)}` : category ? `?category=${category}` : ''}`),
  stream:   (username) => req('GET', `/api/streams/${username}`),

  // Streaming
  goLive: (body) => req('POST', '/api/streams/go-live', body),
  endStream: ()    => req('POST', '/api/streams/end', {}),

  // Tips
  tip: (username, body) => req('POST', `/api/tip/${username}`, body),

  // Subscriptions
  subscribe:   (username, body) => req('POST',   `/api/subscribe/${username}`, body),
  unsubscribe: (username)       => req('DELETE',  `/api/subscribe/${username}`),

  // Follow
  follow:    (username) => req('POST',   `/api/follow/${username}`),
  unfollow:  (username) => req('DELETE',  `/api/follow/${username}`),
  following: ()         => req('GET',    '/api/following'),

  // Search & Profile
  search:  (q)        => req('GET', `/api/search?q=${encodeURIComponent(q)}`),
  profile: (username) => req('GET', `/api/profile/${username}`),

  // Raids
  raid:        (username) => req('POST', `/api/raid/${username}`),
  liveChannels: ()        => req('GET',  '/api/channels/live'),

  // Notifications
  notifications:      ()     => req('GET',    '/api/notifications'),
  markRead:           (id)   => req('POST',   '/api/notifications/read', id ? { id } : {}),
  deleteNotification: (id)   => req('DELETE', `/api/notifications/${id}`),

  // VODs
  vod: (id) => req('GET', `/api/vod/${id}`),

  // Clips
  createClip:    (body)     => req('POST', '/api/clips', body),
  clip:          (id)       => req('GET',  `/api/clips/${id}`),
  channelClips:  (username) => req('GET',  `/api/clips/channel/${username}`),

  // Moderation
  listBans:   (channel)          => req('GET',    `/api/mod/${channel}/bans`),
  banUser:    (channel, body)    => req('POST',   `/api/mod/${channel}/ban`, body),
  unbanUser:  (channel, username)=> req('DELETE', `/api/mod/${channel}/ban/${username}`),
  listMods:   (channel)          => req('GET',    `/api/mod/${channel}/mods`),
  addMod:     (channel, body)    => req('POST',   `/api/mod/${channel}/mods`, body),
  removeMod:  (channel, username)=> req('DELETE', `/api/mod/${channel}/mods/${username}`),

  // Channel Points
  earnPoints:      (username)        => req('POST',   `/api/points/watch/${username}`),
  getPoints:       (username)        => req('GET',    `/api/points/${username}`),
  listRewards:     (username)        => req('GET',    `/api/rewards/${username}`),
  createReward:    (body)            => req('POST',   '/api/rewards', body),
  updateReward:    (id, body)        => req('PUT',    `/api/rewards/${id}`, body),
  deleteReward:    (id)              => req('DELETE', `/api/rewards/${id}`),
  redeemReward:    (rewardId)        => req('POST',   `/api/redeem/${rewardId}`),
  redemptions:     ()                => req('GET',    '/api/redemptions'),
  fulfillRedemption: (id)            => req('POST',   `/api/redemptions/${id}/fulfill`),
  rejectRedemption:  (id)            => req('POST',   `/api/redemptions/${id}/reject`),

  // Dashboard
  dashboard: () => req('GET', '/api/dashboard'),

  // Dashboard chart
  dashboardChart: (stat, period) => req('GET', `/api/dashboard/chart?stat=${stat}&period=${period}`),

  // Stripe onboarding
  stripeOnboard: (body) => req('POST', '/api/streamer/stripe/onboard', body),
  stripeStatus:  ()     => req('GET',  '/api/streamer/stripe/status'),

  // Settings
  updateProfile:  (body) => req('PUT',  '/api/settings/profile', body),
  changePassword: (body) => req('PUT',  '/api/settings/password', body),

  // 2FA
  get2fa:      ()     => req('GET',  '/api/settings/2fa'),
  setup2fa:    (body) => req('POST', '/api/settings/2fa/setup', body),
  verify2fa:   (body) => req('POST', '/api/settings/2fa/verify', body),
  disable2fa:  ()     => req('POST', '/api/settings/2fa/disable'),
  verify2faLogin: (body) => req('POST', '/api/auth/2fa/verify', body),

  // Image uploads
  uploadAvatar: (file) => uploadFile('/api/upload/avatar', file),
  uploadBanner: (file) => uploadFile('/api/upload/banner', file),

  // Payouts
  updatePayout:   (body) => req('PUT',  '/api/settings/payout', body),
  requestPayout:  ()     => req('POST', '/api/payout/request'),
  payoutHistory:  ()     => req('GET',  '/api/payout/history'),

  // Chat Settings
  chatSettings:       (username) => req('GET', `/api/chat-settings/${username}`),
  updateChatSettings: (body)     => req('PUT', '/api/chat-settings', body),

  // Categories
  categories: () => req('GET', '/api/categories'),

  // Featured & Recommendations
  featured:        () => req('GET', '/api/featured'),
  recommendations: () => req('GET', '/api/recommendations'),

  // VOD Chat & Mute
  vodChat:         (vodId) => req('GET', `/api/vod/${vodId}/chat`),
  addVodMute:      (vodId, body) => req('POST', `/api/vod/${vodId}/mute`, body),
  removeVodMute:   (vodId, muteId) => req('DELETE', `/api/vod/${vodId}/mute/${muteId}`),

  // Polls
  createPoll:  (body) => req('POST', '/api/polls', body),
  activePoll:  (username) => req('GET', `/api/polls/${username}/active`),
  votePoll:    (id, body) => req('POST', `/api/polls/${id}/vote`, body),
  endPoll:     (id) => req('POST', `/api/polls/${id}/end`),

  // Channel Panels
  panels:       (username) => req('GET', `/api/panels/${username}`),
  createPanel:  (body) => req('POST', '/api/panels', body),
  updatePanel:  (id, body) => req('PUT', `/api/panels/${id}`, body),
  deletePanel:  (id) => req('DELETE', `/api/panels/${id}`),

  // Gift Subs
  giftSub: (username, body) => req('POST', `/api/gift-sub/${username}`, body),

  // Content Ratings
  rateStream:    (username, body) => req('POST', `/api/rate/${username}`, body),
  streamRatings: (username) => req('GET', `/api/ratings/${username}`),

  // Stream Goals
  updateGoal: (body) => req('PUT', '/api/streams/goal', body),

  // DMCA / Copyright
  dmcaClaim:     (body)     => req('POST', '/api/dmca', body),
  adminDmca:     (status)   => req('GET',  `/api/admin/dmca?status=${status || 'pending'}`),
  adminResolveDmca: (id, body) => req('PUT', `/api/admin/dmca/${id}`, body),
  adminAddStrike:   (username, body) => req('POST', `/api/admin/strike/${username}`, body),
  adminGetStrikes:  (username) => req('GET', `/api/admin/strikes/${username}`),

  // Reports
  report: (body) => req('POST', '/api/report', body),

  // Admin
  adminReports:     (status) => req('GET', `/api/admin/reports?status=${status || 'pending'}`),
  adminResolve:     (id, body) => req('PUT', `/api/admin/reports/${id}`, body),
  adminWarn:        (username, body) => req('POST', `/api/admin/warn/${username}`, body),
  adminSuspend:     (username, body) => req('POST', `/api/admin/suspend/${username}`, body),
  adminUnsuspend:   (username) => req('POST', `/api/admin/unsuspend/${username}`),
  adminUserHistory: (username) => req('GET', `/api/admin/history/${username}`),

  // Admin Payouts
  adminPayouts:          (status) => req('GET', `/api/admin/payouts?status=${status || 'pending'}`),
  adminProcessPayout:    (id, body) => req('PUT', `/api/admin/payouts/${id}`, body),
  adminGeneratePayouts:  () => req('POST', '/api/admin/payouts/generate'),
};
